import { useState } from "react";

import { useTranslation } from "react-i18next";
import { useNavigate } from "react-router-dom";

import { ApiError } from "@/api/client";
import {
  useDeletePlanningPolicy,
  type PlanningPolicy,
} from "@/api/planningPolicy";
import { Button } from "@/components/ui/button";
import {
  Dialog,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";

interface Props {
  policy: PlanningPolicy | null;
  onClose: () => void;
}

/**
 * If the policy has 0 members: plain confirm + delete.
 * If the policy has members: API returns 409; we show a block dialog
 * pointing the planner at the Planning Config page.
 */
export function DeletePolicyDialog({ policy, onClose }: Props) {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const del = useDeletePlanningPolicy();
  const [blockedCount, setBlockedCount] = useState<number | null>(null);

  if (!policy) return null;

  async function handleDelete() {
    setBlockedCount(null);
    try {
      await del.mutateAsync(policy!.id);
      onClose();
    } catch (err) {
      if (err instanceof ApiError && err.status === 409) {
        setBlockedCount(
          (err.error?.memberCount as number | undefined) ?? policy!.memberCount,
        );
      } else {
        throw err;
      }
    }
  }

  if (blockedCount !== null) {
    return (
      <Dialog open onOpenChange={(open) => !open && onClose()}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>
              {t("planningPolicies.delete.blockedTitle")}
            </DialogTitle>
          </DialogHeader>
          <p className="text-sm">
            {t("planningPolicies.delete.blockedDescription", {
              name: policy.name,
              count: blockedCount,
            })}
          </p>
          <DialogFooter>
            <Button
              variant="outline"
              onClick={() => {
                onClose();
                navigate(`/items?policy=${policy.id}`);
              }}
            >
              {t("planningPolicies.delete.showMembers")}
            </Button>
            <Button onClick={onClose}>{t("common.buttons.confirm")}</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    );
  }

  return (
    <Dialog open onOpenChange={(open) => !open && onClose()}>
      <DialogContent>
        <DialogHeader>
          <DialogTitle>
            {t("planningPolicies.delete.confirmTitle", { name: policy.name })}
          </DialogTitle>
        </DialogHeader>
        <p className="text-sm">
          {t("planningPolicies.delete.confirmDescription", {
            count: policy.memberCount,
          })}
        </p>
        <DialogFooter>
          <Button variant="ghost" onClick={onClose}>
            {t("common.buttons.cancel")}
          </Button>
          <Button
            variant="destructive"
            disabled={del.isPending}
            onClick={handleDelete}
          >
            {t("common.buttons.delete")}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
